const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/reducers/userReducer.ts';
import Imm from 'immutable';
import {
    BIOMETRIC_SCAN,
    CHECK_USERPIN,
    ICheckUserPinAction,
    LOAD_USER_ACCESSVALUES,
    LOAD_USER_RIGHTS,
    LoadAccessValuesAction,
    LoadUserRightsAction,
    SET_USERNAMEISVISUM,
    SET_USERPIN,
} from '../actions/userActions';
import { Logger } from '../services/KisLoggerService';
import * as UserDefs from '../defs/userDefs';
import { AsyncProcessStatus } from '../defs/generalDefs';

const Log = Logger('common/userReducer');

/**
 * The initial values of this reducer's branch (common values only).
 */
export const initialState: any = Imm.fromJS({
    accessValues: {},
    apiToken: {},
    biometricScanStatus: AsyncProcessStatus.IDLE,
    blockInfos: [],
    persdata: {},
    pinCheck: {},
    record: {},
    rights: {},
});

/**
 * The actual reducer
 */
export function userReducer(state = initialState, action: any = {}) {
    switch (action.type) {
        case BIOMETRIC_SCAN.pending:
            return state.set('biometricScanStatus', AsyncProcessStatus.PENDING);
        case BIOMETRIC_SCAN.success:
            return state.set('biometricScanStatus', AsyncProcessStatus.SUCCESS);
        case BIOMETRIC_SCAN.failure:
            return state.set('biometricScanStatus', AsyncProcessStatus.FAILURE);

        case SET_USERPIN.pending:
            return state;
        case SET_USERPIN.success:
            return state
                .setIn(['record', 'ETAG'], action.meta.etag)
                .setIn(['record', 'SPECIAL', 'PIN'], action.payload.SPECIAL.PIN);
        case SET_USERPIN.failure:
            return state;

        case CHECK_USERPIN.pending:
            return checkUserPinPending(state, action);
        case CHECK_USERPIN.success:
            return checkUserPinSuccess(state, action);
        case CHECK_USERPIN.failure:
            return checkUserPinFailure(state, action);

        case SET_USERNAMEISVISUM:
            return state.set('usernameIsVisum', action.payload.usernameIsVisum);

        case LOAD_USER_RIGHTS.pending:
            return loadUserRightsPending(state, action);
        case LOAD_USER_RIGHTS.success:
            return loadUserRightsSuccess(state, action);
        case LOAD_USER_RIGHTS.failure:
            return loadUserRightsFailure(state, action);

        case LOAD_USER_ACCESSVALUES.success:
            return loadUserAccessValuesSuccess(state, action);
        case LOAD_USER_ACCESSVALUES.failure:
            return loadUserAccessValuesFailure(state, action);

        default:
            return state;
    }
}

function loadUserRightsPending(state: Imm.Map<string, any>, action: LoadUserRightsAction) {
    action.meta.accessCodes.forEach(code => {
        if (!code || code === UserDefs.ANYBODY || code === UserDefs.NOBODY || code === UserDefs.PARENT_RIGHTS) {
            // Log.warn (logChannel, `loadUserRightsPending: Illegal code: ${code}`);
        } else {
            state = state.setIn(['rights', code], UserDefs.ACCESS_LOADING);
        }
    });
    return state;
}

function loadUserRightsSuccess(state: Imm.Map<string, any>, action: LoadUserRightsAction) {
    action.meta.accessCodes.forEach(code => {
        if (!code || code === UserDefs.ANYBODY || code === UserDefs.NOBODY || code === UserDefs.PARENT_RIGHTS) {
            Log.warn(`loadUserRightsSuccess: Illegal code: ${code}`);
        } else {
            const result = action.payload && action.payload.Result ? action.payload.Result[code] : null;

            if (result === '1' || result === 'Y') {
                state = state.setIn(['rights', code], UserDefs.ACCESS_GRANTED);
            } else if (result === '0' || result === 'N') {
                state = state.setIn(['rights', code], UserDefs.ACCESS_DENIED);
            } else {
                Log.warn(`loadUserRightsSuccess: Bad reply (${result}) for code: ${code}`);
                state = state.setIn(['rights', code], UserDefs.ACCESS_FAILURE);
            }
        }
    });
    return state;
}

function loadUserRightsFailure(state: Imm.Map<string, any>, action: LoadUserRightsAction) {
    action.meta.accessCodes.forEach(code => {
        if (!code || code === UserDefs.ANYBODY || code === UserDefs.NOBODY || code === UserDefs.PARENT_RIGHTS) {
            Log.warn(`loadUserRightsFailure: Illegal code: ${code}`);
        } else {
            state = state.setIn(['rights', code], UserDefs.ACCESS_FAILURE);
        }
    });
    return state;
}

function checkUserPinPending(state: Imm.Map<string, any>, action: ICheckUserPinAction) {
    const id = `${action.meta.visum}_${action.meta.pin}`;
    return state.setIn(['pinCheck', id], 'loading');
}

function checkUserPinSuccess(state: Imm.Map<string, any>, action: ICheckUserPinAction) {
    const id = `${action.meta.visum}_${action.meta.pin}`;
    const granted = action.payload && action.payload.Match === '1';
    return state.setIn(['pinCheck', id], granted ? 'grant' : 'deny');
}

function checkUserPinFailure(state: Imm.Map<string, any>, action: ICheckUserPinAction) {
    // We come here if visum is not a valid user, or if the user does not have a prefs entry yet.
    const id = `${action.meta.visum}_${action.meta.pin}`;
    return state.setIn(['pinCheck', id], 'deny');
}

function loadUserAccessValuesSuccess(state: Imm.Map<string, any>, action: LoadAccessValuesAction) {
    const payload = Imm.fromJS(action?.payload?.Result || {});

    // We iterate over meta.accessValues rather than payload, because we want to make sure we store something for all
    // access values we've looked up even if it's an empty string, in order to be able to distinguish whether we've
    // already tried to load the access value, or whether the user just does not have any value for it.

    action.meta.accessValues.forEach(name => {
        const value = payload.get(name) || '';
        state = state.setIn(['accessValues', name], value);
    });

    return state;
}

function loadUserAccessValuesFailure(state: Imm.Map<string, any>, action: LoadAccessValuesAction) {
    // Store empty strings in the state, so we can later see whether we've already tried to load it or not.

    Log.warn(`Failed to load access values, storing them as empty string: ${action.meta.accessValues.join(', ')}`);

    action.meta.accessValues.forEach(name => {
        state = state.setIn(['accessValues', name], '');
    });

    return state;
}
